import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { EditorView, ViewUpdate, keymap, highlightSpecialChars, drawSelection, dropCursor, rectangularSelection, crosshairCursor, lineNumbers, highlightActiveLineGutter, WidgetType, Decoration, DecorationSet } from '@codemirror/view'
import { EditorState, Extension, StateEffect, StateField, EditorSelection, SelectionRange } from '@codemirror/state'
import { markdown } from '@codemirror/lang-markdown'
import { oneDark } from '@codemirror/theme-one-dark'
import { autocompletion, acceptCompletion, completionKeymap } from '@codemirror/autocomplete'
import { searchKeymap, search, highlightSelectionMatches } from '@codemirror/search'
import { lintKeymap } from '@codemirror/lint'
import { defaultKeymap, history, historyKeymap, indentWithTab, indentMore, indentLess } from '@codemirror/commands'
import { bracketMatching, indentOnInput } from '@codemirror/language'
import CodeMirror from '@uiw/react-codemirror'
import { motion, AnimatePresence } from 'framer-motion'
import clsx from 'clsx'

import { useEditorStore } from '@stores/editorStore'
import { useAIStore } from '@stores/aiStore'
import { useDocumentStore } from '@stores/documentStore'
import { useEditorSettings } from '@stores/editorSettingsStore'
import { useReviewStore } from '@stores/reviewStore'
import type { AIHighlight, TextSelection, DocumentChange } from '@/types'
import SelectionToolbar from './SelectionToolbar'
import FloatingAIChat, { FloatingAIChatHandle } from './FloatingAIChat'
import LaTeXToolbar from './latex-toolbar/LaTeXToolbar'
import { insertBold, insertItalic, insertUnderline, toggleComment } from '@/utils/latex/insertionHelpers'
import { scientificAutocompletion } from '@/utils/scientificAutocompletion'
import { citationAutocompletion, clearCitationCache } from '@/utils/citationAutocomplete'
import { imageAutocompletion } from '@/utils/imageAutocomplete'
import { latexCommandAutocompletion } from '@/utils/latexCommandAutocomplete'
import { createLaTeXAutocompleteProvider, latexLinter } from '@/utils/latex'
import { tabStopNavigation } from '@/utils/latex/tabStopNavigation'
import { latexSupport } from '@/utils/latexSupport'
import { latexTheme } from '@/utils/latexTheme'
// Removed aiAPI import - using store's processAITask instead
import { useAuthorship } from '@/hooks/useAuthorship'
import { useQueryClient } from '@tanstack/react-query'
import { logError, logWarn, logDebug } from '@utils/logger'
import AIProcessingIndicator from './AIProcessingIndicator'
import {
  detectBibliographyType,
  generateBibitemEntry,
  insertBibitemIntoDocument,
  needsBibliographyEnvironment
} from '@/utils/latexBibliography'
import { generateBibtexEntry } from '@/utils/generateBibtex'
import { latexAPI } from '@/api/latex'
import toast from 'react-hot-toast'

// Interface for remote cursor data
interface RemoteCursor {
  userId: string
  username: string
  full_name?: string
  position: number
  color: string
}

interface ScientificEditorProps {
  value: string
  onChange: (value: string) => void
  onSelectionChange?: (selection: TextSelection | null) => void
  onCursorChange?: (position: number) => void // Callback for cursor position changes
  onViewReady?: (view: EditorView | null) => void // Callback when editor view is ready
  onSave?: () => void | Promise<void> // Callback for save (Ctrl+S)
  onSaveAndCompile?: () => void | Promise<void> // Callback for save and compile (Ctrl+2)
  remoteCursors?: RemoteCursor[] // Other users' cursors to display
  documentId?: string | null // Document ID for authorship tracking
  blameEnabled?: boolean // Whether git blame feature is enabled
  theme?: 'light' | 'dark'
  mode?: 'markdown' | 'latex' | 'plain'
  readOnly?: boolean
  className?: string
  onOpenReviewPanel?: () => void // Callback to open review panel
  // Navigation and highlighting for warnings/errors
  highlightLine?: { lineNumber: number; type: 'warning' | 'error' } | null
  onNavigateToLine?: (lineNumber: number) => void // Callback when navigation is requested
}

// Custom highlighting effect for AI suggestions
const highlightEffect = StateEffect.define<{ from: number; to: number; class: string }>()

const highlightField = StateField.define<{ from: number; to: number; class: string }[]>({
  create() {
    return []
  },
  update(highlights, tr) {
    for (const effect of tr.effects) {
      if (effect.is(highlightEffect)) {
        return [...highlights, effect.value]
      }
    }
    return highlights
  },
})

// Widget for displaying remote user cursors
class RemoteCursorWidget extends WidgetType {
  constructor(
    private username: string,
    private fullName: string | undefined,
    private color: string
  ) {
    super()
  }

  toDOM() {
    const name = this.fullName || this.username
    const initials = name.length >= 2
      ? name.substring(0, 2).toUpperCase()
      : name.toUpperCase().padEnd(2, name[0] || 'U')

    const container = document.createElement('span')
    container.className = 'cm-remote-cursor'
    container.setAttribute('aria-label', `${name}'s cursor`)

    const avatar = document.createElement('span')
    avatar.className = `inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-medium text-white ${this.color} border-2 border-white shadow-sm`
    avatar.textContent = initials
    avatar.style.marginLeft = '-2px'
    avatar.style.marginTop = '-1px'
    avatar.style.zIndex = '10'
    avatar.title = `${name} is typing here`

    container.appendChild(avatar)
    return container
  }

  ignoreEvent() {
    return false
  }
}

// Effect for updating remote cursors
const setRemoteCursorsEffect = StateEffect.define<RemoteCursor[]>()

// Comment Highlighting
const setCommentsEffect = StateEffect.define<{ from: number; to: number; id: string }[]>()
const commentHighlightField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none
  },
  update(decorations, tr) {
    decorations = decorations.map(tr.changes)
    for (const effect of tr.effects) {
      if (effect.is(setCommentsEffect)) {
        const marks = effect.value.map(({ from, to, id }) =>
          Decoration.mark({
            class: 'bg-yellow-200/50 border-b-2 border-yellow-400 cursor-pointer hover:bg-yellow-300/50 transition-colors',
            attributes: { 'data-thread-id': id }
          }).range(from, to)
        )
        return Decoration.set(marks, true)
      }
    }
    return decorations
  },
  provide: f => EditorView.decorations.from(f)
})

// Track Changes Highlighting (Insertions)
const setTrackChangesEffect = StateEffect.define<{ from: number; to: number; id: string }[]>()
const trackChangesField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none
  },
  update(decorations, tr) {
    decorations = decorations.map(tr.changes)
    for (const effect of tr.effects) {
      if (effect.is(setTrackChangesEffect)) {
        const marks = effect.value.map(({ from, to, id }) =>
          Decoration.mark({
            class: 'bg-green-200/50 border-b-2 border-green-400 hover:bg-green-300/50 transition-colors',
            attributes: { 'data-change-id': id }
          }).range(from, to)
        )
        return Decoration.set(marks, true)
      }
    }
    return decorations
  },
  provide: f => EditorView.decorations.from(f)
})

// Warning/Error Line Highlighting
const setWarningErrorHighlightEffect = StateEffect.define<{ lineNumber: number; type: 'warning' | 'error' } | null>()
const warningErrorHighlightField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none
  },
  update(decorations, tr) {
    decorations = decorations.map(tr.changes)
    for (const effect of tr.effects) {
      if (effect.is(setWarningErrorHighlightEffect)) {
        const highlight = effect.value
        if (!highlight) {
          return Decoration.none
        }
        
        try {
          // Find the line in the document (line numbers are 1-indexed)
          const doc = tr.state.doc
          if (highlight.lineNumber < 1 || highlight.lineNumber > doc.lines) {
            return Decoration.none
          }
          
          const line = doc.line(highlight.lineNumber)
          
          // Highlight the entire line with appropriate color
          const lineClass = highlight.type === 'error' 
            ? 'bg-red-200/70 dark:bg-red-900/50 border-l-4 border-red-500' 
            : 'bg-yellow-200/70 dark:bg-yellow-900/50 border-l-4 border-yellow-500'
          
          const mark = Decoration.line({
            class: lineClass
          })
          
          return Decoration.set([mark.range(line.from)])
        } catch (error) {
          // Line doesn't exist or invalid
          return Decoration.none
        }
      }
    }
    return decorations
  },
  provide: f => EditorView.decorations.from(f)
})

const ScientificEditor: React.FC<ScientificEditorProps> = ({
  value,
  onChange,
  onSelectionChange,
  onCursorChange,
  onViewReady,
  onSave,
  onSaveAndCompile,
  remoteCursors = [],
  documentId,
  blameEnabled = false,
  theme = 'light',
  mode = 'markdown',
  readOnly = false,
  className,
  onOpenReviewPanel,
  highlightLine,
  onNavigateToLine,
}) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)
  const [mounted, setMounted] = useState(false)
  const [viewReady, setViewReady] = useState(false)
  const previousValueRef = useRef<string>(value)
  const chatRef = useRef<FloatingAIChatHandle>(null)

  // Store hooks
  const {
    selection,
    setSelection,
    aiHighlights,
    addAIHighlight,
    removeAIHighlight,
    cursorPosition,
    setCursorPosition
  } = useEditorStore()

  const { isProcessing, currentTask, processAITask: processAITaskFromStore } = useAIStore()
  const { currentDocument } = useDocumentStore()
  const { settings: editorSettings } = useEditorSettings()
  const { comments, trackChanges, activeTab, setActiveThreadId, activeThreadId, createThread, setActiveTab } = useReviewStore()
  const queryClient = useQueryClient()
  const [currentTaskType, setCurrentTaskType] = useState<string | null>(null)

  // Debug: Log when isProcessing changes
  useEffect(() => {
    logDebug('[ScientificEditor] isProcessing changed:', isProcessing, 'currentTaskType:', currentTaskType)
  }, [isProcessing, currentTaskType])

  // Authorship tracking for git blame feature
  const docIdForAuthorship = documentId || currentDocument?.id
  const { authorship, isLoading: isLoadingAuthorship, fetchAuthorship, clearAuthorship } = useAuthorship({
    documentId: docIdForAuthorship,
    enabled: !!docIdForAuthorship && blameEnabled // Only enable when toggle is on
  })

  // Track mouse position for authorship hover
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number; position: number } | null>(null)

  // Debug: Log when documentId changes
  useEffect(() => {
    logDebug('[ScientificEditor] documentId for authorship:', docIdForAuthorship)
  }, [docIdForAuthorship])

  // Clear authorship when blame is disabled
  useEffect(() => {
    if (!blameEnabled) {
      setHoverPosition(null)
      clearAuthorship()
    }
  }, [blameEnabled, clearAuthorship])

  // Sync editor content when value prop changes externally (e.g., from refresh/polling)
  // This handles cases where CodeMirror doesn't automatically detect prop changes
  useEffect(() => {
    if (viewRef.current && value !== previousValueRef.current) {
      const view = viewRef.current
      const currentContent = view.state.doc.toString()

      // Only update if the value actually changed and is different from current editor content
      // This prevents infinite loops when onChange triggers value updates
      if (value !== currentContent) {
        logDebug('[ScientificEditor] External value change detected, updating editor:', {
          newValueLength: value.length,
          currentContentLength: currentContent.length,
          isDifferent: value !== currentContent
        })

        // Save cursor position before update
        const selection = view.state.selection.main
        const cursorPos = selection.head

        // Update the editor content
        view.dispatch({
          changes: {
            from: 0,
            to: view.state.doc.length,
            insert: value
          },
          // Restore cursor position (clamped to new document length)
          selection: EditorSelection.cursor(Math.min(cursorPos, value.length))
        })

        previousValueRef.current = value
      } else {
        // Value prop changed but content is same - just update ref
        previousValueRef.current = value
      }
    } else if (!viewRef.current) {
      // Editor not mounted yet - just update ref
      previousValueRef.current = value
    }
  }, [value])

  // Selection toolbar state
  const [showSelectionToolbar, setShowSelectionToolbar] = useState(false)
  const [toolbarPosition, setToolbarPosition] = useState({ x: 0, y: 0 })
  const toolbarRef = useRef<HTMLDivElement>(null)

  // State field for remote cursors (created per component instance)
  const remoteCursorsField = useMemo(() => StateField.define<DecorationSet>({
    create() {
      return Decoration.none
    },
    update(cursors, tr) {
      cursors = cursors.map(tr.changes)

      for (const effect of tr.effects) {
        if (effect.is(setRemoteCursorsEffect)) {
          const decorations = effect.value.map(cursor =>
            Decoration.widget({
              widget: new RemoteCursorWidget(
                cursor.username,
                cursor.full_name,
                cursor.color
              ),
              side: 1, // Place widget after the cursor position
            }).range(cursor.position)
          )
          return Decoration.set(decorations)
        }
      }

      return cursors
    },
    provide: f => EditorView.decorations.from(f)
  }), [])

  // Extensions for the editor
  const extensions = useCallback((): Extension[] => {
    const baseExtensions = [
      lineNumbers(),
      highlightActiveLineGutter(),
      highlightSpecialChars(),
      history(),
      drawSelection(),
      dropCursor(),
      EditorState.allowMultipleSelections.of(true),
      indentOnInput(),
      bracketMatching(),
      autocompletion({
        activateOnTyping: editorSettings.autocompleteActivateOnTyping,
        maxRenderedOptions: editorSettings.autocompleteMaxOptions,
        defaultKeymap: false, // Disable default keymap - we handle Tab ourselves
        // Trigger autocomplete when backslash is typed in LaTeX mode
        activateOnTypingDelay: 0,
        override: [
          // In LaTeX mode, use new production-grade autocomplete system
          ...(mode === 'latex' ? [
            createLaTeXAutocompleteProvider(documentId || undefined),
            // Add citation autocomplete if documentId is available
            ...(documentId ? [
              citationAutocompletion(documentId),
              imageAutocompletion(documentId),
            ] : []),
          ] : []),
          // Then add scientific autocompletion (which won't interfere with LaTeX commands)
          scientificAutocompletion,
        ],
      }),
      rectangularSelection(),
      crosshairCursor(),
      highlightSelectionMatches(),
      search(),
      // Tab stop navigation (must come BEFORE completionKeymap to intercept Tab)
      // tabStopNavigation() returns an Extension (array), so we can spread it directly
      ...(mode === 'latex' ? (() => {
        try {
          const ext = tabStopNavigation()
          return Array.isArray(ext) ? ext : []
        } catch (e) {
          console.error('[ScientificEditor] Error creating tabStopNavigation:', e)
          return []
        }
      })() : []),
      // Add Tab handler directly in keymap for LaTeX mode
      // IMPORTANT: This keymap MUST come BEFORE the other keymap.of() below
      // CodeMirror processes keymaps in order, and the first match wins
      ...(mode === 'latex' ? (() => {
        const customKeymap = keymap.of([
          {
            key: 'Tab',
            preventDefault: true,
            run: (view: EditorView) => {
              console.log('[ScientificEditor] Direct Tab handler called!', {
                timestamp: Date.now(),
                viewExists: !!view,
                stateExists: !!view?.state
              })
              // Check for autocomplete first
              const autocompletePanels = view.dom.querySelectorAll('.cm-tooltip-autocomplete')
              console.log('[ScientificEditor] Found autocomplete panels:', autocompletePanels.length)

              for (const panel of Array.from(autocompletePanels)) {
                const style = window.getComputedStyle(panel)
                const isVisible = style.display !== 'none' &&
                  style.visibility !== 'hidden' &&
                  parseFloat(style.opacity) > 0
                console.log('[ScientificEditor] Panel visibility:', {
                  display: style.display,
                  visibility: style.visibility,
                  opacity: style.opacity,
                  isVisible
                })

                if (isVisible) {
                  console.log('[ScientificEditor] Autocomplete is visible, accepting...')
                  const accepted = acceptCompletion(view)
                  console.log('[ScientificEditor] acceptCompletion result:', accepted)
                  if (accepted) {
                    console.log('[ScientificEditor] Completion accepted!')
                    return true
                  }
                }
              }

              // No autocomplete, just indent
              console.log('[ScientificEditor] No autocomplete found, defaulting to indent')
              return indentMore(view)
            }
          },
          {
            key: 'Shift-Tab',
            preventDefault: true,
            run: (view: EditorView) => {
              console.log('[ScientificEditor] Shift-Tab handler called!', {
                timestamp: Date.now()
              })
              return indentLess(view)
            }
          },
          {
            key: 'Ctrl-Space',
            preventDefault: true,
            run: (view: EditorView) => {
              console.log('[ScientificEditor] Ctrl-Space handler called (test)!', {
                timestamp: Date.now()
              })
              // This should trigger autocomplete
              return false
            }
          },
          // Formatting keyboard shortcuts (only in LaTeX mode)
          {
            key: 'Mod-b', // Ctrl+B (Windows/Linux) or Cmd+B (Mac)
            preventDefault: true,
            run: (view: EditorView) => {
              insertBold(view)
              return true
            }
          },
          {
            key: 'Mod-i', // Ctrl+I (Windows/Linux) or Cmd+I (Mac)
            preventDefault: true,
            run: (view: EditorView) => {
              insertItalic(view)
              return true
            }
          },
          {
            key: 'Mod-u', // Ctrl+U (Windows/Linux) or Cmd+U (Mac)
            preventDefault: true,
            run: (view: EditorView) => {
              insertUnderline(view)
              return true
            }
          },
          // Save shortcut (Ctrl+S or Cmd+S)
          {
            key: 'Mod-s',
            preventDefault: true,
            run: (view: EditorView) => {
              if (onSave) {
                onSave()
                return true
              }
              return false
            }
          },
          // Save and Compile shortcut (Ctrl+2 or Cmd+2)
          {
            key: 'Mod-2',
            preventDefault: true,
            run: (view: EditorView) => {
              if (onSaveAndCompile) {
                onSaveAndCompile()
                return true
              }
              return false
            }
          },
          // Equation shortcut (Ctrl+M or Cmd+M)
          {
            key: 'Mod-m',
            preventDefault: true,
            run: (view: EditorView) => {
              // Trigger equation button click via event
              const equationButton = document.querySelector('[aria-label="Insert equation"]') as HTMLButtonElement
              if (equationButton && !equationButton.disabled) {
                equationButton.click()
                return true
              }
              return false
            }
          },
          // Comment toggle shortcut (Ctrl+/ or Cmd+/)
          {
            key: 'Mod-/',
            preventDefault: true,
            run: (view: EditorView) => {
              toggleComment(view)
              return true
            }
          },
        ])
        return [customKeymap]
      })() : []),
      keymap.of([
        // Tab key handling is now in tabStopNavigation extension
        // Include completion keymap but filter out Tab (we handle it in tabStopNavigation)
        ...completionKeymap.filter(binding => {
          const key = binding.key || ''
          const isTab = key === 'Tab' || key === 'Shift-Tab'
          if (isTab && mode === 'latex') {
            console.log('[ScientificEditor] Filtering out Tab from completionKeymap, mode =', mode)
          }
          return !isTab || mode !== 'latex'
        }),
        // Default keymaps (but filter out Tab in LaTeX mode since we handle it)
        ...(mode === 'latex'
          ? defaultKeymap.filter(binding => {
            const key = binding.key || ''
            return key !== 'Tab' && key !== 'Shift-Tab'
          })
          : defaultKeymap),
        ...searchKeymap,
        ...historyKeymap,
        ...lintKeymap,
      ]),
      highlightField,
      commentHighlightField,
      trackChangesField,
      warningErrorHighlightField, // Warning/error line highlighting
      remoteCursorsField, // Remote user cursors
      // Enable line wrapping
      EditorView.lineWrapping,
      // Custom theme
      theme === 'dark' ? oneDark : [],
      // Editor styling from settings
      EditorView.theme({
        '&': {
          fontSize: `${editorSettings.fontSize}px`,
          fontFamily: editorSettings.fontFamily,
        },
        '.cm-content': {
          lineHeight: typeof editorSettings.lineHeight === 'number'
            ? `${editorSettings.lineHeight}`
            : editorSettings.lineHeight === 'compact'
              ? '1.4'
              : editorSettings.lineHeight === 'comfortable'
                ? '1.8'
                : '1.6',
        },
        '.cm-editor': {
          height: '100%',
        },
        '.cm-scroller': {
          fontFamily: editorSettings.fontFamily,
        },
      }),
    ]

    // Add language support based on mode
    if (mode === 'markdown') {
      baseExtensions.push(markdown())
    } else if (mode === 'latex') {
      baseExtensions.push(latexSupport())
      // Add LaTeX syntax highlighting theme
      baseExtensions.push(latexTheme(theme === 'dark'))
      // Add LaTeX linting (chktex)
      baseExtensions.push(latexLinter())
    }

    // Add update listener
    baseExtensions.push(
      EditorView.updateListener.of((update: ViewUpdate) => {
        if (update.docChanged) {
          onChange(update.state.doc.toString())
        }

        // Handle selection changes
        if (update.selectionSet) {
          const selection = update.state.selection.main
          if (selection.from !== selection.to) {
            const selectedText = update.state.doc.sliceString(selection.from, selection.to)
            const lineFrom = update.state.doc.lineAt(selection.from).number
            const lineTo = update.state.doc.lineAt(selection.to).number

            const textSelection: TextSelection = {
              start: selection.from,
              end: selection.to,
              text: selectedText,
              lineStart: lineFrom,
              lineEnd: lineTo,
            }

            setSelection(textSelection)
            onSelectionChange?.(textSelection)

            // Show selection toolbar
            if (selectedText.trim().length > 0) {
              showSelectionToolbarAt(update.view, selection)
            } else {
              setShowSelectionToolbar(false)
            }
          } else {
            setSelection(null)
            onSelectionChange?.(null)
            setShowSelectionToolbar(false)
            const cursorPos = selection.from
            setCursorPosition(cursorPos)

            // Notify parent component of cursor position change (for WebSocket)
            if (onCursorChange) {
              onCursorChange(cursorPos)
            }
          }
        }
      })
    )

    // Add mouse hover listener for authorship tracking and click listener for comments
    baseExtensions.push(
      EditorView.domEventHandlers({
        click(event, view) {
          const target = event.target as HTMLElement
          const threadId = target.getAttribute('data-thread-id')
          if (threadId) {
            setActiveThreadId(threadId)
          }
        },
        mousemove(event, view) {
          // Only process if blame is enabled
          if (!blameEnabled) {
            return
          }

          // Use closure to capture current documentId
          const docId = docIdForAuthorship
          if (!docId) {
            return
          }

          // Get position from mouse coordinates relative to editor view
          const rect = view.dom.getBoundingClientRect()
          const x = event.clientX - rect.left
          const y = event.clientY - rect.top

          const coords = view.posAtCoords({ x, y })

          // posAtCoords returns a number (position) or null
          if (coords !== null && typeof coords === 'number') {
            const position = coords

            setHoverPosition({
              x: x,
              y: y,
              position
            })

            // Fetch authorship for this position
            fetchAuthorship(position)
          }
        },
        mouseleave() {
          // Clear authorship when mouse leaves editor (only if blame is enabled)
          if (blameEnabled) {
            setHoverPosition(null)
            clearAuthorship()
          }
        }
      })
    )

    return baseExtensions
  }, [theme, mode, onChange, onSelectionChange, onCursorChange, setSelection, setCursorPosition, docIdForAuthorship, fetchAuthorship, clearAuthorship, blameEnabled, editorSettings, documentId])

  // Update remote cursors when they change
  useEffect(() => {
    if (viewRef.current && remoteCursors.length >= 0) {
      viewRef.current.dispatch({
        effects: setRemoteCursorsEffect.of(remoteCursors)
      })
    }
  }, [remoteCursors])

  // Handle warning/error line highlighting and navigation
  useEffect(() => {
    const view = viewRef.current
    if (!view) {
      // Clear highlight if view is not ready
      if (!highlightLine) return
      // Wait for view to be ready
      return
    }

    if (!highlightLine) {
      // Clear highlight
      view.dispatch({
        effects: setWarningErrorHighlightEffect.of(null)
      })
      return
    }

    const { lineNumber, type } = highlightLine
    
    // Validate line number (line numbers are 1-indexed in CodeMirror)
    if (lineNumber < 1 || lineNumber > view.state.doc.lines) {
      logDebug('[ScientificEditor] Invalid line number for highlight:', lineNumber, 'total lines:', view.state.doc.lines)
      return
    }

    // Set the highlight
    view.dispatch({
      effects: setWarningErrorHighlightEffect.of({ lineNumber, type })
    })

    // Navigate to the line
    try {
      const line = view.state.doc.line(lineNumber)
      if (line) {
        // Scroll to the line and place cursor at the start
        view.dispatch({
          selection: EditorSelection.cursor(line.from),
          effects: EditorView.scrollIntoView(line.from, { y: 'center' })
        })
        view.focus()
        
        logDebug('[ScientificEditor] Navigated to line', { lineNumber, type, lineFrom: line.from, lineTo: line.to })
      }
    } catch (error) {
      logError('[ScientificEditor] Error navigating to line:', error)
    }
  }, [highlightLine, viewReady])

  // Update comments and track changes highlights
  useEffect(() => {
    const view = viewRef.current
    if (!view) return

    // Debounce the update to avoid excessive dispatches
    const timeoutId = setTimeout(() => {
      const docString = view.state.doc.toString()

      // Comment Highlights
      const highlights: { from: number; to: number; id: string }[] = []
      comments.forEach(comment => {
        // Safe navigation for anchor
        if (comment.anchor && typeof comment.anchor === 'object' && 'quote' in comment.anchor) {
          const quote = (comment.anchor as any).quote
          if (quote && typeof quote === 'string') {
            const index = docString.indexOf(quote)
            if (index !== -1) {
              highlights.push({
                from: index,
                to: index + quote.length,
                id: comment.id
              })
            }
          }
        }
      })
      view.dispatch({
        effects: setCommentsEffect.of(highlights)
      })

      // Track Changes Highlights
      const changeHighlights: { from: number; to: number; id: string }[] = []
      if (activeTab === 'track-changes' && trackChanges) {
        trackChanges.forEach(changeSet => {
          // Access `changes` if available (ensure backend sends it or store populates it)
          // Note: DocumentChangeSet interface now has changes: DocumentChange[]
          if (changeSet.changes) {
            let currentOffset = 0 // Track cumulative offset for position calculation
            changeSet.changes.forEach((change: DocumentChange, idx: number) => {
              // Use actual position from change data, not string matching
              if (change.type === 'insert' && change.content) {
                // Use the position directly from the change
                const from = change.position
                const to = change.position + (change.content?.length || change.length || 0)
                if (from >= 0 && to <= docString.length) {
                  changeHighlights.push({
                    from,
                    to,
                    id: `${changeSet.id}-${idx}`
                  })
                }
              } else if (change.type === 'delete') {
                // For deletions, highlight the position where deletion occurred
                const from = change.position
                const to = change.position + change.length
                if (from >= 0 && to <= docString.length) {
                  changeHighlights.push({
                    from,
                    to,
                    id: `${changeSet.id}-${idx}`
                  })
                }
              }
            })
          }
        })
      }
      view.dispatch({
        effects: setTrackChangesEffect.of(changeHighlights)
      })

    }, 100)

    return () => clearTimeout(timeoutId)
  }, [comments, trackChanges, activeTab, value]) // Recalculate when data changes

  // Show selection toolbar at cursor position with smart positioning
  const showSelectionToolbarAt = useCallback((view: EditorView, selection: SelectionRange) => {
    const coords = view.coordsAtPos(selection.from)
    if (coords && editorRef.current) {
      const rect = editorRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const windowWidth = window.innerWidth
      const toolbarHeight = 60 // Approximate toolbar height
      const toolbarPadding = 10 // Padding from edges

      // Get selection midpoint for better positioning
      const selectionCoords = view.coordsAtPos(selection.to)
      const selectionMidX = (coords.left + (selectionCoords?.left || coords.left)) / 2
      const selectionMidY = (coords.top + (selectionCoords?.top || coords.top)) / 2

      // Estimate toolbar width (will be measured after render)
      // Use a reasonable estimate: ~600px for full toolbar with all buttons
      const estimatedToolbarWidth = 600
      const minToolbarWidth = 400 // Minimum width even on mobile

      // Calculate available space
      const spaceAbove = selectionMidY - rect.top
      const spaceBelow = windowHeight - selectionMidY
      const spaceLeft = selectionMidX - rect.left
      const spaceRight = rect.right - selectionMidX

      // Determine vertical position (above or below)
      const shouldPositionAbove = spaceAbove > toolbarHeight + toolbarPadding
      const yPosition = shouldPositionAbove
        ? selectionMidY - rect.top - toolbarHeight - toolbarPadding
        : selectionMidY - rect.top + toolbarPadding

      // Determine horizontal position (left or right of selection)
      // Try to center on selection, but adjust if it would overflow
      let xPosition = selectionMidX - rect.left - (estimatedToolbarWidth / 2)

      // Check if toolbar would overflow right edge
      if (xPosition + estimatedToolbarWidth > rect.width - toolbarPadding) {
        // Position to the left of selection, aligned to right edge
        xPosition = rect.width - estimatedToolbarWidth - toolbarPadding
      }

      // Check if toolbar would overflow left edge
      if (xPosition < toolbarPadding) {
        // Position to the right of selection, aligned to left edge
        xPosition = toolbarPadding
      }

      // Ensure toolbar stays within editor bounds
      xPosition = Math.max(toolbarPadding, Math.min(xPosition, rect.width - minToolbarWidth - toolbarPadding))

      setToolbarPosition({
        x: xPosition,
        y: yPosition,
      })
      setShowSelectionToolbar(true)

      // After toolbar is rendered, adjust position based on actual width
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        if (toolbarRef.current && editorRef.current) {
          const toolbarRect = toolbarRef.current.getBoundingClientRect()
          const editorRect = editorRef.current.getBoundingClientRect()
          const actualToolbarWidth = toolbarRect.width

          // Recalculate with actual width
          let adjustedX = selectionMidX - editorRect.left - (actualToolbarWidth / 2)

          // Check right edge overflow
          if (adjustedX + actualToolbarWidth > editorRect.width - toolbarPadding) {
            adjustedX = editorRect.width - actualToolbarWidth - toolbarPadding
          }

          // Check left edge overflow
          if (adjustedX < toolbarPadding) {
            adjustedX = toolbarPadding
          }

          // Ensure we don't go negative
          adjustedX = Math.max(toolbarPadding, adjustedX)

          // Only update if position changed significantly (avoid jitter)
          if (Math.abs(adjustedX - xPosition) > 5) {
            setToolbarPosition(prev => ({ ...prev, x: adjustedX }))
          }
        }
      })
    }
  }, [])

  // Apply AI highlights to the editor
  const applyAIHighlights = useCallback(() => {
    if (!viewRef.current) return

    const effects = aiHighlights.map(highlight =>
      highlightEffect.of({
        from: highlight.start,
        to: highlight.end,
        class: `text-ai-${highlight.type}`,
      })
    )

    if (effects.length > 0) {
      viewRef.current.dispatch({ effects })
    }
  }, [aiHighlights])

  // Handle AI processing
  const handleAITask = useCallback(async (taskType: string, text: string, instructions?: string) => {
    if (!selection) return

    // Special handling for 'explain' task - use chat instead of popup
    if (taskType === 'explain') {
      // Set current task type for indicator
      setCurrentTaskType(taskType)

      try {
        // Get document ID for token tracking
        const docId = documentId || currentDocument?.id

        // Call AI store's processAITask which updates isProcessing state
        const response = await processAITaskFromStore(
          { text, instructions, document_id: docId || undefined },
          taskType as any
        )

        // Open chat with context and initial messages
        if (chatRef.current) {
          const userMessage = `Explain this: ${text}`
          await chatRef.current.openWithContext(text, userMessage, response.result)
          // Show notification
          chatRef.current.showNotification()
        }

        // Invalidate token usage queries after successful AI operation
        if (docId) {
          queryClient.invalidateQueries({ queryKey: ['token-usage', 'document', docId] })
        }
        queryClient.invalidateQueries({ queryKey: ['token-usage', 'user-stats'] })

      } catch (error) {
        logError('AI processing error:', error)
      } finally {
        // Reset task type when done
        setCurrentTaskType(null)
      }
      return
    }

    // For other tasks (proofread, rewrite, expand, summarize), use the popup approach
    // Set current task type for indicator
    setCurrentTaskType(taskType)

    // Remove any existing suggestions for the same selection to prevent stacking
    const existingSuggestions = aiHighlights.filter(
      h => h.type === 'suggestion' &&
        h.start === selection.start &&
        h.end === selection.end
    )
    existingSuggestions.forEach(h => removeAIHighlight(h.id))

    // Add processing highlight
    const processingId = `processing-${Date.now()}`
    const processingHighlight: AIHighlight = {
      id: processingId,
      start: selection.start,
      end: selection.end,
      type: 'selection',
      originalText: text,
    }
    addAIHighlight(processingHighlight)

    try {
      // Get document ID for token tracking
      const docId = documentId || currentDocument?.id

      // Call AI store's processAITask which updates isProcessing state
      const response = await processAITaskFromStore(
        { text, instructions, document_id: docId || undefined },
        taskType as any
      )

      // Remove processing highlight
      removeAIHighlight(processingId)

      // Add suggestion highlight with better ID
      const suggestionHighlight: AIHighlight = {
        id: `suggestion-${selection.start}-${selection.end}-${Date.now()}`,
        start: selection.start,
        end: selection.end,
        type: 'suggestion',
        originalText: text,
        suggestedText: response.result,
        confidence: 0.95,
      }
      addAIHighlight(suggestionHighlight)

      // Invalidate token usage queries after successful AI operation
      if (docId) {
        queryClient.invalidateQueries({ queryKey: ['token-usage', 'document', docId] })
      }
      queryClient.invalidateQueries({ queryKey: ['token-usage', 'user-stats'] })

    } catch (error) {
      logError('AI processing error:', error)
      // Remove processing highlight on error
      removeAIHighlight(processingId)
    } finally {
      // Reset task type when done
      setCurrentTaskType(null)
    }
  }, [selection, addAIHighlight, removeAIHighlight, documentId, currentDocument?.id, queryClient, aiHighlights, processAITaskFromStore])

  // Accept AI suggestion
  const acceptAISuggestion = useCallback((highlight: AIHighlight) => {
    if (!viewRef.current || !highlight.suggestedText) {
      logDebug('Accept failed - no view or suggested text:', {
        hasView: !!viewRef.current,
        hasSuggestedText: !!highlight.suggestedText
      })
      return
    }

    const view = viewRef.current
    logDebug('Accepting suggestion:', {
      from: highlight.start,
      to: highlight.end,
      suggestedText: highlight.suggestedText
    })

    try {
      const transaction = view.state.update({
        changes: {
          from: highlight.start,
          to: highlight.end,
          insert: highlight.suggestedText,
        },
      })

      view.dispatch(transaction)
      logDebug('Transaction dispatched successfully')
      removeAIHighlight(highlight.id)
    } catch (error) {
      logError('Error accepting suggestion:', error)
    }
  }, [removeAIHighlight])

  // Reject AI suggestion
  const rejectAISuggestion = useCallback((highlight: AIHighlight) => {
    removeAIHighlight(highlight.id)
  }, [removeAIHighlight])

  useEffect(() => {
    setMounted(true)
  }, [])

  // Add direct keydown listener to handle Tab key
  useEffect(() => {
    if (!mounted || !viewReady || !viewRef.current) {
      return
    }

    const view = viewRef.current
    const editorElement = view.dom
    const currentMode = mode // Capture current mode

    const handleKeyDown = (e: Event) => {
      const ke = e as KeyboardEvent

      // Only handle Tab key in LaTeX mode
      if (ke.key === 'Tab' && currentMode === 'latex') {
        // Prevent default browser behavior
        ke.preventDefault()
        ke.stopPropagation()

        // Check for autocomplete first
        const autocompletePanels = view.dom.querySelectorAll('.cm-tooltip-autocomplete')

        for (const panel of Array.from(autocompletePanels)) {
          const style = window.getComputedStyle(panel)
          const isVisible = style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            parseFloat(style.opacity) > 0

          if (isVisible) {
            const accepted = acceptCompletion(view)
            if (accepted) {
              return // Don't continue to indent
            }
          }
        }

        // No autocomplete, indent instead
        if (ke.shiftKey) {
          indentLess(view)
        } else {
          indentMore(view)
        }
      }
    }

    editorElement.addEventListener('keydown', handleKeyDown, true) // Use capture phase

    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [mounted, mode, viewReady])

  useEffect(() => {
    applyAIHighlights()
  }, [applyAIHighlights])

  if (!mounted) {
    return (
      <div className={clsx('flex items-center justify-center h-full', className)}>
        <div className="spinner w-8 h-8 text-primary-600" />
      </div>
    )
  }

  return (
    <div ref={editorRef} className={clsx('relative h-full w-full overflow-hidden', className)}>
      {/* Main Editor */}
      <CodeMirror
        value={value}
        onChange={onChange}
        extensions={extensions()}
        theme={theme}
        readOnly={readOnly}
        className="h-full w-full editor-container"
        basicSetup={false}
        placeholder="Start writing your document here... You can use Markdown syntax and LaTeX math notation like $E=mc^2$"
        onCreateEditor={(view) => {
          viewRef.current = view
          setViewReady(true)
          // Notify parent component that view is ready
          if (onViewReady) {
            onViewReady(view)
          }
        }}
      />

      {/* Selection Toolbar */}
      <AnimatePresence>
        {showSelectionToolbar && selection && (
          <motion.div
            ref={toolbarRef}
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-40 bg-white shadow-lg rounded-lg border border-gray-200 p-2"
            style={{
              left: `${toolbarPosition.x}px`,
              top: `${toolbarPosition.y}px`,
              maxWidth: 'calc(100% - 20px)', // Ensure it doesn't exceed container width
            }}
          >
            <SelectionToolbar
              selectedText={selection.text}
              selectionStart={selection.start}
              selectionEnd={selection.end}
              onAITask={handleAITask}
              isProcessing={isProcessing}
              documentContext={value}
              onComment={async ({ anchor, body }) => {
                if (documentId) {
                  try {
                    // Create the comment thread
                    await createThread(anchor, body)
                    // Switch to comments tab and open review panel
                    setActiveTab('comments')
                    if (onOpenReviewPanel) {
                      onOpenReviewPanel()
                    }
                  } catch (error) {
                    logError('[ScientificEditor] Failed to create comment:', error)
                    toast.error('Failed to create comment')
                  }
                }
              }}
              onCitationInsert={async (citation, paper) => {
                if (viewRef.current && paper) {
                  // Get fresh selection from current editor state
                  const view = viewRef.current
                  const currentSelection = view.state.selection.main
                  const currentContent = view.state.doc.toString()

                  // Use the end of the selection (or cursor position)
                  const insertPosition = currentSelection.to

                  // Ensure position is within document bounds
                  const docLength = view.state.doc.length
                  const safePosition = Math.min(insertPosition, docLength)

                  // Generate BibTeX key if not provided
                  let bibtexKey = paper.bibtexKey
                  if (!bibtexKey) {
                    // Generate key from paper metadata
                    const firstAuthor = paper.authors?.[0]?.split(/\s+/).pop()?.toLowerCase() || 'unknown'
                    const year = paper.year ? String(paper.year) : 'noyear'
                    const titleWords = paper.title?.split(/\s+/).slice(0, 2) || []
                    const titlePart = titleWords.map((w: string) => w.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 4)).join('')
                    bibtexKey = `${firstAuthor}${year}${titlePart}`.substring(0, 30)
                  }

                  if (mode === 'latex') {
                    // LaTeX mode: Handle both internal and external bibliography
                    const bibInfo = detectBibliographyType(currentContent)

                    // Insert \cite{key} at selection
                    const citationText = `\\cite{${bibtexKey}}`

                    // Prepare changes array for batch update
                    const changes: Array<{ from: number; to: number; insert: string }> = [
                      {
                        from: safePosition,
                        to: safePosition,
                        insert: citationText
                      }
                    ]

                    // If internal bibliography, add \bibitem entry
                    if (bibInfo.type === 'internal' && bibInfo.bibliographyEnd !== undefined) {
                      const bibitemEntry = generateBibitemEntry(paper, bibtexKey)
                      // Insert before \end{thebibliography}
                      changes.push({
                        from: bibInfo.bibliographyEnd,
                        to: bibInfo.bibliographyEnd,
                        insert: `  ${bibitemEntry}\n`
                      })
                    } else if (bibInfo.type === 'external') {
                      // External bibliography: add BibTeX entry to .bib file
                      const docId = documentId || currentDocument?.id
                      if (docId) {
                        try {
                          const bibtexEntry = generateBibtexEntry(paper, bibtexKey)
                          await latexAPI.addBibtexEntry(docId, bibtexEntry)
                          // Clear citation cache so autocomplete shows new citation
                          clearCitationCache(docId)
                        } catch (error) {
                          logError('Error adding BibTeX entry to .bib file:', error)
                        }
                      } else {
                        logWarn('No documentId available to add BibTeX entry')
                      }
                    } else if (bibInfo.type === 'none') {
                      // No bibliography exists - create .bib file and add entry
                      const docId = documentId || currentDocument?.id
                      if (docId) {
                        try {
                          const bibtexEntry = generateBibtexEntry(paper, bibtexKey)
                          await latexAPI.addBibtexEntry(docId, bibtexEntry)
                          // Clear citation cache so autocomplete shows new citation
                          clearCitationCache(docId)
                        } catch (error) {
                          logError('Error creating .bib file:', error)
                        }
                      } else {
                        logWarn('No bibliography found. Citation \\cite{' + bibtexKey + '} inserted but bibliography entry not added. Please add bibliography manually.')
                      }
                    }

                    try {
                      view.dispatch({
                        changes: changes,
                        selection: { anchor: safePosition + citationText.length }
                      })

                      // Trigger onChange to update document
                      const newContent = view.state.doc.toString()
                      onChange(newContent)
                    } catch (error) {
                      logError('Error inserting citation:', error)
                      // Fallback: try to insert at document end
                      view.dispatch({
                        changes: {
                          from: docLength,
                          insert: citationText
                        },
                        selection: { anchor: docLength + citationText.length }
                      })
                    }
                  } else {
                    // Non-LaTeX: Insert formatted citation text
                    const citationText = ` ${citation}`
                    try {
                      view.dispatch({
                        changes: {
                          from: safePosition,
                          insert: citationText
                        },
                        selection: { anchor: safePosition + citationText.length }
                      })
                    } catch (error) {
                      logError('Error inserting citation:', error)
                      // Fallback: try to insert at document end
                      view.dispatch({
                        changes: {
                          from: docLength,
                          insert: citationText
                        },
                        selection: { anchor: docLength + citationText.length }
                      })
                    }
                  }
                }
              }}
              documentMode={mode}
              documentId={documentId || currentDocument?.id}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Suggestions Overlay */}
      <AnimatePresence mode="wait">
        {aiHighlights
          .filter(h => h.type === 'suggestion' && h.suggestedText)
          .map(highlight => {
            // Get proper coordinates from the editor view
            const coords = viewRef.current?.coordsAtPos(highlight.start)
            const editorRect = editorRef.current?.getBoundingClientRect()

            let left = 20
            let top = 20

            if (coords && editorRect) {
              left = coords.left - editorRect.left + 20
              top = coords.top - editorRect.top + 30

              // Ensure the suggestion doesn't go off-screen
              const maxLeft = window.innerWidth - 400 // max-w-md is ~28rem = ~448px
              const maxTop = window.innerHeight - 400
              left = Math.min(left, maxLeft)
              top = Math.min(top, maxTop)
              left = Math.max(left, 20)
              top = Math.max(top, 20)
            }

            return (
              <motion.div
                key={highlight.id}
                initial={{ opacity: 0, scale: 0.9, y: -20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 25,
                }}
                className="fixed z-50 bg-white dark:bg-gray-800 border-2 border-green-300 dark:border-green-600 rounded-lg shadow-2xl max-w-md max-h-96 overflow-auto"
                style={{
                  left: `${left}px`,
                  top: `${top}px`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="flex items-center gap-2 mb-3"
                  >
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <div className="text-sm text-green-800 dark:text-green-300 font-semibold">
                      AI Suggestion ({Math.round((highlight.confidence || 0) * 100)}% confidence)
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-sm text-gray-700 dark:text-gray-300 mb-3 p-3 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 max-h-48 overflow-y-auto"
                  >
                    {highlight.suggestedText}
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="flex gap-2 justify-end"
                  >
                    <button
                      onClick={() => rejectAISuggestion(highlight)}
                      className="text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => acceptAISuggestion(highlight)}
                      className="text-sm bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors shadow-md hover:shadow-lg"
                    >
                      Accept
                    </button>
                  </motion.div>
                </div>
              </motion.div>
            )
          })
        }
      </AnimatePresence>

      {/* AI Processing Indicator */}
      <AIProcessingIndicator
        isVisible={isProcessing}
        taskType={currentTaskType || undefined}
      />

      {/* Floating AI Chat */}
      <FloatingAIChat
        ref={chatRef}
        documentId={currentDocument?.id}
        cursorPosition={cursorPosition}
      />

      {/* Authorship Tooltip (Git Blame) - VSCode style ghost text */}
      <AnimatePresence>
        {blameEnabled && hoverPosition && authorship && authorship.user_id && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="absolute z-50 pointer-events-none"
            style={{
              left: `${hoverPosition.x + 15}px`,
              top: `${hoverPosition.y - 10}px`,
            }}
          >
            <div className="bg-gray-900 text-gray-100 text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {authorship.full_name || authorship.username || 'Unknown'}
                </span>
                {authorship.created_at && (
                  <span className="text-gray-400">
                    {new Date(authorship.created_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            {/* Arrow pointing to text */}
            <div className="absolute -left-1 top-2 w-0 h-0 border-t-4 border-t-transparent border-r-4 border-r-gray-900 border-b-4 border-b-transparent" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ScientificEditor